import React, { useContext, useCallback, useEffect, useState } from 'react';
import { Box, Select, MenuItem, Button, FormControl, Checkbox } from '@mui/material';
import { LoadPanel } from 'devextreme-react';
import moment from 'moment';

import { StaffApis } from '../../../../hooks/apis/StaffApis';
import { IEmployeeDetails } from '../../../../interfaces/StaffClocking/IEmployeeDetails';
import { INewClocking } from '../../../../interfaces/StaffClocking/INewClocking';
import { AccessDataContext } from '../../../../contextProviders/AccessDataContext';
import PageTitle from '../../../components/PageTitle';
import './StaffClocking.scss';

const position = { of: '.staffClocking' };

const StaffClocking = () => {
  const { user } = useContext(AccessDataContext);

  const { postNewClocking, getDepartments, getOffices, getEmployeeDetails } = StaffApis();
  const [employeeDetails, setEmployeeDetails] = useState<IEmployeeDetails | undefined>(null);
  const [userChoices, setUserChoices] = useState<INewClocking | undefined>(null);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [errors, setErrors] = useState([]);
  const [submittingClocking, setSubmittingClocking] = useState<boolean>(false);
  const [loadingUserData, setLoadingUserData] = useState<boolean>(true);

  const collectUserData = useCallback(() => {
    if (user?.ReferenceNumber) {
      getEmployeeDetails(user.ReferenceNumber).then((data) => {
        setEmployeeDetails(data);
      }).finally(() => {
        setLoadingUserData(false); 
      });
    }
  }, [user.ReferenceNumber, getEmployeeDetails])

  useEffect(() => {
    collectUserData();
  }, [collectUserData]);

  const submitClocking = useCallback(() => {
    const errorMessage = [];
    if (!userChoices?.Department) {
      errorMessage.push('Department required');
    }

    if (!userChoices?.Office) {
      errorMessage.push('Office required');
    }

    if (!userChoices?.UserId) {
      errorMessage.push('UserId required');
    }

    if (!userChoices?.ReferenceNumber) {
      errorMessage.push('ReferenceNumber required');
    }

    if (errorMessage.length > 0) {
      setErrors(errorMessage);
    } else {
      setSubmittingClocking(true);
      postNewClocking(userChoices).then(() => collectUserData()).finally(() => setSubmittingClocking(false));
    }
  }, [userChoices, setErrors, postNewClocking, collectUserData]);

  useEffect(() => {
    if (employeeDetails && user.Id) {
      setUserChoices((current) => ({
        ...current,
        UserId: user?.Id,
        ReferenceNumber: employeeDetails?.ReferenceNumber,
        Department: employeeDetails?.Department,
        Office: employeeDetails?.Office,
        WorkingRemotely: false
      }));
    }
  }, [user.Id, employeeDetails, setUserChoices]);

  useEffect(() => {
    getDepartments().then((data) => setDepartments(data));
  }, [getDepartments]);

  useEffect(() => {
    getOffices().then((data) => setOffices(data));
  }, [getOffices]);

  return (
    <>
      <PageTitle pageTitle="Staff Clocking" />

      <Box className="staffClocking">
        <LoadPanel height={100} width={250} position={position} message="Loading User details" visible={loadingUserData} />
        <LoadPanel height={100} width={250} position={position} message="Submitting clocking" visible={submittingClocking} />

        <h2>{employeeDetails?.FullName}</h2>
        <h3>Employee Reference: {employeeDetails?.ReferenceNumber}</h3>

        {employeeDetails?.LastOut && employeeDetails?.LastIn ? (
          <>
            <p>
              <b>Last in:</b> {employeeDetails?.LastIn ? moment(employeeDetails.LastIn).format("DD-MM-yyyy HH:mm:ss A") : null}
            </p>
            <p>
              <b>Last out:</b> {employeeDetails?.LastOut ? moment(employeeDetails?.LastOut).format("DD-MM-yyyy HH:mm:ss A") : null}
            </p>

            <FormControl fullWidth>
              <label className="small">
                Working Remotely
                <Checkbox
                  checked={userChoices?.WorkingRemotely || false}
                  onChange={(e) => {
                    setUserChoices((current) => ({ ...current, WorkingRemotely: e.target.checked }));
                  }}
                />
              </label>
            </FormControl>

            <FormControl fullWidth>
              <label>Department</label>
              <Select
                size="small"
                value={userChoices?.Department || employeeDetails.Department}
                onChange={(item) => setUserChoices((current) => ({ ...current, Department: item.target.value }))}>
                {departments?.map((m) => {
                  return (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <label>Location</label>
              <Select
                size="small"
                value={userChoices?.Office || employeeDetails.Office}
                onChange={(item) =>
                  setUserChoices((current) => ({
                    ...current,
                    Office: item?.target?.value.toString(),
                  }))
                }>
                {offices?.map((m) => {
                  return (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Button className="siteBtn" onClick={() => submitClocking()}>
              Submit
            </Button>
          </>
        ) : (
          <>
            <p>
              <b>Clocked In:</b> {employeeDetails?.LastIn ? moment(employeeDetails?.LastIn).format("DD-MM-yyyy HH:mm:ss A") : null}
            </p>
            <Button className="siteBtn" onClick={() => submitClocking()}>
              Clockout
            </Button>
          </>
        )}

        {errors ?? (
          <Box>
            {errors.map((m) => (
              <p key={m}>{m}</p>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default StaffClocking;
